import { useLayoutEffect, useRef, useState, type ComponentProps } from 'react';
import { MediaViewer, fromManagedImage } from '@/workspace/media';
import {
  ManagedImageProjection,
  refusedImageProjection,
} from '../../../artifacts/images/projection.mjs';
import type { ManagedImageProjection as ImageProjection } from '../../../artifacts/images/projection.mjs';
import { ImageRatio } from '../../../artifacts/images/values.mjs';
import type { AppMediaDeclaration } from '../../../contracts/app-media.mjs';
import type { AppSdk } from '../tier0/index.mjs';

export interface ImageViewerEntry {
  key: string;
  image: ImageProjection;
  ratio: AppMediaDeclaration['ratio'];
  changedSince?: boolean | null;
}
export interface ImageViewerInput {
  /** Same SDK/document lifetime as the current authorized projections. */
  lifetime: Pick<AppSdk, 'onInvalidate'>;
  images: readonly ImageViewerEntry[];
  inspectOn: boolean;
  phone?: ComponentProps<typeof MediaViewer>['phone'];
  locale?: ComponentProps<typeof MediaViewer>['locale'];
}
type Selection = {
  key: string;
  lifetime: ImageViewerInput['lifetime'];
  origin?: HTMLElement;
};

function viewerItem(entry: ImageViewerEntry) {
  const parsed = ManagedImageProjection.safeParse(entry.image);
  const ratio = ImageRatio.safeParse(entry.ratio);
  return fromManagedImage(
    parsed.success && ratio.success ? parsed.data : refusedImageProjection(),
    {
      key: entry.key,
      ratio: ratio.success ? ratio.data : '1:1',
      changedSince: entry.changedSince === true,
    },
  );
}

/** Selection only. Pixels, gestures and presentation belong to DES2's one MediaViewer. */
export function useImageViewer(input: ImageViewerInput) {
  const current = useRef(input);
  current.current = input;
  const retired = useRef({ lifetime: input.lifetime, value: false });
  const [selection, setSelection] = useState<Selection | null>(null);
  const selectionNow = useRef(selection);
  selectionNow.current = selection;
  const [, refresh] = useState(0);
  useLayoutEffect(() => {
    const lifetime = input.lifetime;
    retired.current = { lifetime, value: false };
    let active = true;
    const unsubscribe = lifetime.onInvalidate(() => {
      if (!active) return;
      retired.current = { lifetime, value: true };
      setSelection(null);
      refresh((epoch) => epoch + 1);
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [input.lifetime]);

  const selected = selection ? input.images.filter((entry) => entry.key === selection.key) : [];
  const index = selected.length === 1 ? input.images.indexOf(selected[0]) : -1;
  const active =
    selection?.lifetime === input.lifetime &&
    !retired.current.value &&
    !input.inspectOn &&
    index >= 0;
  useLayoutEffect(() => {
    if (selection && !active) setSelection(null);
  }, [selection, active]);
  const close = () => setSelection(null);
  const open = (key: string, origin?: HTMLElement) => {
    const now = current.current;
    if (
      now.lifetime !== input.lifetime ||
      now.inspectOn ||
      retired.current.lifetime !== now.lifetime ||
      retired.current.value
    )
      return;
    const matches = now.images.filter((entry) => entry.key === key);
    if (matches.length !== 1 || !ManagedImageProjection.safeParse(matches[0].image).success) return;
    setSelection({ key, lifetime: now.lifetime, origin });
  };
  const viewer =
    active && selection ? (
      <MediaViewer
        items={input.images.map(viewerItem)}
        index={index}
        onClose={() => setSelection((now) => (now === selection ? null : now))}
        onIndexChange={(next) => {
          if (selectionNow.current !== selection) return;
          const entry = input.images[next];
          if (entry) open(entry.key);
        }}
        origin={(at) => (at === index ? (selection.origin ?? null) : null)}
        phone={input.phone}
        locale={input.locale}
      />
    ) : null;
  return { open, close, viewer };
}
